import { useTranslation } from "react-i18next";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Lightbulb, ExternalLink, Play } from "lucide-react";
import { useTour } from "@/hooks/useTour";
import type { HelpTopic as HelpTopicDef } from "./types";

interface HelpTopicProps {
	sectionId: string;
	topic: HelpTopicDef;
}

export const HelpTopic = ({ sectionId, topic }: HelpTopicProps) => {
	const { t } = useTranslation();
	const tour = useTour();

	const title = t(topic.titleKey);
	const overview = t(topic.overviewKey);
	const steps = topic.stepsKey
		? (t(topic.stepsKey, { returnObjects: true, defaultValue: [] }) as unknown as string[])
		: [];
	const tips = topic.tipsKey
		? (t(topic.tipsKey, { returnObjects: true, defaultValue: [] }) as unknown as string[])
		: [];

	const handleAction = () => {
		if (!topic.action) return;
		if (topic.action.type === "restartTour")
			tour.start();
		else if (topic.action.type === "link" && topic.action.target)
			window.location.href = topic.action.target;
		else if (topic.action.type === "route" && topic.action.target)
			window.location.assign(topic.action.target);
	};

	const actionIcon = topic.action?.type === "restartTour" ? Play : ExternalLink;
	const ActionIcon = actionIcon;

	return (
		<AccordionItem value={`${sectionId}.${topic.id}`} id={`help-${sectionId}.${topic.id}`}>
			<AccordionTrigger className="text-left hover:no-underline">
				<span className="text-base font-semibold">{title}</span>
			</AccordionTrigger>
			<AccordionContent>
				<div className="space-y-4 pr-8">
					<p className="text-sm text-muted-foreground leading-relaxed">{overview}</p>

					{Array.isArray(steps) && steps.length > 0 && (
						<ol className="list-decimal pl-5 space-y-1.5 text-sm">
							{steps.map((step, i) => (
								<li key={i} className="leading-relaxed">{step}</li>
							))}
						</ol>
					)}

					{Array.isArray(tips) && tips.length > 0 && (
						<div className="rounded-md border bg-muted/40 p-3">
							<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
								<Lightbulb className="h-3.5 w-3.5" />
								<span>{t("help.tipsLabel", "Tips")}</span>
							</div>
							<ul className="list-disc pl-5 space-y-1 text-sm">
								{tips.map((tip, i) => (
									<li key={i} className="leading-relaxed">{tip}</li>
								))}
							</ul>
						</div>
					)}

					{topic.action && (
						<div>
							<Button variant="outline" size="sm" onClick={handleAction}>
								<ActionIcon className="h-4 w-4 mr-2" />
								{t(topic.action.labelKey)}
							</Button>
						</div>
					)}
				</div>
			</AccordionContent>
		</AccordionItem>
	);
};
